import { Ban, CheckCircle2, FolderOpen } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { PersonnelDocumentTypeModal } from '../components/PersonnelDocumentTypeModal';
import { personnelDocumentTypesApi } from '../personnelDocumentTypesApi';
import type { PersonnelDocumentType } from '../personnelDocumentTypes';

type TabKey = 'info';

/**
 * One row of the document checklist.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — a name, a note, and the trail of who changed it — not a record whose
 * content splits into panels a reader chooses between.
 *
 * It exists because every index page in this product is partnered with a profile, and here the
 * partner earns its keep: the count of employees who have already filed a document of this
 * kind is what a reader wants before deciding whether to rename or delete the row.
 */
export function PersonnelDocumentTypeProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [type, setType] = useState<PersonnelDocumentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      personnelDocumentTypesApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setType(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this document.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Personnel',
    { label: 'Settings', to: '/personnel/settings' },
    { label: 'Documents', to: '/personnel/settings/documents' },
    type?.name ?? 'Document',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !type) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="personnel-document-type-profile"
        header={
          <ProfileHeader
            id="personnel-document-type-profile-header"
            icon={<FolderOpen className="h-6 w-6" aria-hidden="true" />}
            title={type.name}
            meta={[
              // Unlabelled, because "12 employees" says what it is on its own.
              `${type.filedByCount} ${type.filedByCount === 1 ? 'employee' : 'employees'}`,
            ]}
            status={
              <StatusText tone={type.isActive ? 'active' : 'inactive'}>
                {type.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={type.createdByName}
            createdAt={type.createdAt}
          />
        }
        banner={
          type.isActive ? (
            <ProfileBanner
              id="personnel-document-type-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This document is asked for: it appears as a row on the Documents panel of every 201 file."
            />
          ) : (
            <ProfileBanner
              id="personnel-document-type-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This document is deleted. It is no longer on the checklist, and the documents already filed against it are kept."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="personnel-document-type-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'personnel-document-types.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: type.name },
              { label: 'Description', value: type.description },
              {
                label: 'Order',
                value: <span className="font-mono tabular-nums">{type.sortOrder}</span>,
              },
              {
                label: 'Employees who filed one',
                value: <span className="font-mono tabular-nums">{type.filedByCount}</span>,
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="personnel-document-type-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: type.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(type.createdAt)} className="font-mono">
                    {formatDateTime(type.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: type.updatedByName },
              {
                label: 'Updated',
                value: type.updatedAt ? (
                  <time dateTime={toIso(type.updatedAt)} className="font-mono">
                    {formatDateTime(type.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <PersonnelDocumentTypeModal
          open
          editing={type}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
